/* eslint-disable no-unused-vars */
// File: frontend/src/components/Topbar.js
import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Avatar, Box, IconButton, Paper, Popover, Typography,
  Button, TextField, Stack, Menu, MenuItem, Divider,
  useMediaQuery, useTheme, Chip, Tooltip, alpha, Badge,
} from "@mui/material";
import {
  DarkMode, LightMode, ExpandMore,
  Menu as MenuIcon, Refresh as RefreshIcon,
  CalendarToday as CalIcon,
  Notifications as NotificationsIcon,
} from "@mui/icons-material";
import { authService } from "../services/api";
import { useColorMode } from "../App";
import { useGlobal } from "../context/GlobalContext";
import { LIGHT, DARK } from "../theme";
import { AppButton, tapTargetSx } from "./ui";

// Quick chips map to dynamic presets in GlobalContext; the context does the
// date math so ranges auto-advance at midnight.
const QUICK = [
  { label: "Today",      preset: "today" },
  { label: "7 days",     preset: "last_7_days" },
  { label: "30 days",    preset: "last_30_days" },
  { label: "90 days",    preset: "last_90_days" },
  { label: "This month", preset: "this_month" },
];

export default function Topbar({ onMenuClick }) {
  const { mode, toggleMode }  = useColorMode();
  const theme                 = useTheme();
  const isMobile              = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall               = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl]             = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const { from, to, setFrom, setTo, dateRangePreset, applyDatePreset, refresh, isRefreshing, topbarNotifications } = useGlobal();

  // ── Transaction focus ──────────────────────────────────────────────────────
  // The date range filters historical reporting. Inside a money-out flow there
  // is no history to filter — there is one transaction being composed — so the
  // control is not merely useless there, it is a live global setting sitting a
  // thumb's width from an amount field. It is hidden for the duration.
  //
  // Hidden, not disabled: a greyed-out control still asks to be read. The
  // selected range itself is untouched, so leaving the flow returns to whatever
  // the operator had chosen.
  const { pathname, search } = useLocation();
  const inTransaction = /^\/wallet\/send(\/|$)/.test(pathname);
  // The Plans tab is a catalogue of what is on sale NOW; it reads no period,
  // and a date range beside it says the catalogue is "1 Sept – 4 Sept". Only
  // the Performance tab of /packages consumes the range, so that is the only
  // one that shows it.
  const onPlans = /^\/packages(\/|$)/.test(pathname) && !/[?&]tab=performance(&|$)/.test(search || "");

  // AGENTS reads no period at all. Every figure on it is the CURRENT position —
  // what agents hold now, what they owe now — and a range beside "outstanding
  // UGX 149,000" invites an owner to read it as five days' trading. That
  // misreading is the specific one the brief calls out.
  //
  // FINANCIALS and REPORTS do read a period, but they carry their OWN period
  // control, because an owner opening a report about August should not thereby
  // re-scope the dashboard they came from. Two date pickers on one screen, one
  // of which does nothing, is worse than either alone.
  const ownsItsOwnPeriod = /^\/(agents|financials|reports)(\/|$)/.test(pathname);

  // READS NO PERIOD AT ALL. Verified against the source: none of these pages
  // destructures `from`/`to` from useGlobal() — Routers.js takes only
  // refreshTick and selectedRouterId, Topology only the selector setters,
  // Roaming and RemoteAccess only selectedRouterId, and Sites does not call
  // useGlobal() at all. They describe the estate as it is RIGHT NOW: which
  // stations are up, how they are wired, who may roam where.
  //
  // The control was rendered on all of them and changed nothing. That is worse
  // than absent — an operator who narrows to "yesterday" and sees the same
  // router list reasonably concludes the list is yesterday's. Same reasoning as
  // the Plans tab above: a date range beside a thing that has no period is a
  // claim about that thing.
  const readsNoPeriod = /^\/(router|routers|sites|network|remote-access)(\/|$)/.test(pathname);

  const hideDateRange = inTransaction || onPlans || ownsItsOwnPeriod || readsNoPeriod;

  const isDark = mode === "dark";

  const user    = authService.getCurrentUser();
  const initial = (user?.username || "A").slice(0, 1).toUpperCase();

  // ── Mode-aware color tokens (derived from the app palette in theme.js so the
  //     chrome follows the Light/Dark toggle with no hardcoded hex) ────────────
  const colors = useMemo(() => {
    const p = isDark ? DARK : LIGHT;
    const accent = p.accent.main;
    return {
      bg:          alpha(p.bg.page, isDark ? 0.92 : 0.93),
      border:      p.border.default,
      hoverBorder: accent,
      hoverColor:  accent,
      accent,
      accentSoft:  alpha(accent, isDark ? 0.1 : 0.06),
      avatarBg:    accent,
      chipBg:      isDark ? DARK.bg.active : LIGHT.bg.sunken,
      chipColor:   p.text.secondary,
      chipBorder:  p.border.default,
      chipHoverBorder: accent,
      chipHoverColor:  accent,
      dateColor:   p.text.primary,
    };
  }, [isDark]);

  const dateLabel = useMemo(() => {
    const fmt = (d, short) => {
      const [y, m, day] = String(d).split("-").map(Number);
      const parsed = y && m && day ? new Date(y, m - 1, day) : new Date(d);
      try { return parsed.toLocaleDateString(undefined, short
        ? { month: "short", day: "numeric" }
        : { month: "short", day: "numeric", year: "numeric" }); }
      catch { return d; }
    };
    // "Sep 1 to Sep 18": words, not a dash (PesaFii copy rule, 18 Sep 2026).
    return isSmall ? `${fmt(from, true)} to ${fmt(to, true)}` : `${fmt(from)} to ${fmt(to)}`;
  }, [from, to, isSmall]);

  // Single shared teardown — see authService.logoutAndRedirect. This used to
  // clear only the credentials while Sidebar also cleared the router; that
  // divergence is exactly how account-scoped state survived a logout.
  const handleLogout = () => { setUserMenuAnchor(null); authService.logoutAndRedirect("/login"); };

  return (
    <Paper
      elevation={0}
      sx={{
        position: "sticky", top: 0, zIndex: 100,
        bgcolor: colors.bg,
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderBottom: "1px solid",
        borderColor: colors.border,
        ...(!isDark && {
          boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
        }),
        px: { xs: 1.75, sm: 3 },
        py: { xs: 1.05, sm: 1.35 },
        borderRadius: 0,
        transition: "background-color 0.25s, border-color 0.25s",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>

        {/* Hamburger — only on mobile, triggers the Sidebar drawer via Layout */}
        {isMobile && (
          <IconButton
            onClick={onMenuClick}
            size="small"
            sx={{
              border: "1px solid",
              borderColor: colors.border,
              borderRadius: 2,
              p: 0.85,
              "&:hover": { borderColor: colors.hoverBorder, color: colors.hoverColor },
              transition: "all 0.2s",
              ...tapTargetSx({ touch: true }),
            }}
          >
            <MenuIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}

        {/* Right side actions — xs gap kept tight so the bell fits alongside the
            other actions on narrow phones without horizontal overflow.
            On mobile this row also FLEXES, so the date button below can take the
            slack the removed refresh icon left behind instead of everything
            staying bunched against the right edge. `minWidth: 0` is what lets a
            flex child shrink at 320px rather than forcing an overflow. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 },
          ml: "auto", flex: { xs: 1, md: "none" }, minWidth: 0 }}>

          {!hideDateRange && <>
          {/* Date range button — the primary control on this bar, so on mobile it
              takes the free space rather than sitting at its content width. */}
          <Button
            onClick={(e) => setAnchorEl(e.currentTarget)}
            variant="outlined" size="small"
            startIcon={<CalIcon sx={{ fontSize: 16 }} />}
            // The chevron is now shown at EVERY width. It used to be dropped on
            // xs to save space, which left the control reading as a static label
            // rather than something tappable — the one affordance that says "this
            // opens a picker". The space it needs is exactly what removing the
            // mobile refresh icon freed up.
            endIcon={<ExpandMore sx={{ fontSize: 16 }} />}
            sx={{
              flex: { xs: 1, md: "none" },
              minWidth: 0,
              justifyContent: { xs: "flex-start", md: "center" },
              maxWidth: { xs: "100%", md: "none" },
              border: "1px solid",
              borderColor: colors.border,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: 12.5, sm: 14 },
              px: { xs: 1.15, sm: 1.75 },
              py: { xs: 0.65, sm: 0.8 },
              color: colors.dateColor,
              bgcolor: "transparent",
              whiteSpace: "nowrap",
              "&:hover": {
                borderColor: colors.hoverBorder,
                color: colors.hoverColor,
                bgcolor: colors.accentSoft,
              },
              transition: "all 0.2s",
            }}
          >
            {dateLabel}
          </Button>
          </>}

          {/* Refresh — bumps the global refreshTick; every page's data loader
              re-runs against the current router/range/tenant scope.
              ── DESKTOP ONLY ──────────────────────────────────────────────────
              Hidden below md so the date range — the control an operator
              actually reaches for — gets that width. This button is
              PRESENTATION ONLY: it calls refresh() from GlobalContext, which
              bumps a tick that pages already respond to. Automatic polling,
              card-level Retry and the browser's own reload are all independent
              of it, so removing it here removes an affordance, never a recovery
              path. Nothing about the desktop control changes. */}
          {!isMobile && (
          <Tooltip title={isRefreshing ? "Refreshing…" : "Refresh"}>
            <IconButton
              onClick={refresh} size="small"
              aria-label="Refresh"
              sx={{
                border: "1px solid",
                borderColor: colors.border,
                borderRadius: 2,
                p: { xs: 0.75, sm: 0.9 },
                "&:hover": { borderColor: colors.hoverBorder, color: colors.hoverColor },
                "&:focus-visible": {
                  outline: `2px solid ${colors.accent}`,
                  outlineOffset: 2,
                },
                transition: "all 0.2s",
              }}
            >
              <RefreshIcon sx={{
                fontSize: 19,
                "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
                animation: isRefreshing ? "spin 0.85s linear infinite" : "none",
              }} />
            </IconButton>
          </Tooltip>
          )}

          {/* Notifications — the ONE global bell. Rendered only when the current
              page registered its alerts via GlobalContext (setTopbarNotifications),
              so there is never a dead button or a duplicate bell elsewhere. */}
          {topbarNotifications && (
            <Tooltip title="Notifications">
              <IconButton
                onClick={topbarNotifications.onOpen} size="small"
                aria-label="Notifications"
                sx={{
                  border: "1px solid",
                  borderColor: colors.border,
                  borderRadius: 2,
                  p: { xs: 0.75, sm: 0.9 },
                  "&:hover": { borderColor: colors.hoverBorder, color: colors.hoverColor },
                  "&:focus-visible": {
                    outline: `2px solid ${colors.accent}`,
                    outlineOffset: 2,
                  },
                  transition: "all 0.2s",
                }}
              >
                <Badge
                  badgeContent={topbarNotifications.count || 0}
                  sx={{ "& .MuiBadge-badge": { fontSize: 9, minWidth: 14, height: 14, bgcolor: "warning.main", color: "rgba(0,0,0,0.87)" } }}
                >
                  <NotificationsIcon sx={{ fontSize: 19 }} />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

          {/* Theme toggle — visible on ALL breakpoints (incl. mobile). The
              touch target stays ≥40px on mobile; the icon is ~19px. */}
          <Tooltip title={isDark ? "Switch to light mode" : "Switch to dark mode"}>
            <IconButton
              onClick={toggleMode}
              size="small"
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              sx={{
                border: "1px solid",
                borderColor: colors.border,
                borderRadius: 2,
                p: { xs: 0.85, sm: 0.9 },
                "&:hover": { borderColor: colors.hoverBorder, color: colors.hoverColor },
                "&:focus-visible": {
                  outline: `2px solid ${colors.accent}`,
                  outlineOffset: 2,
                },
                transition: "all 0.2s",
              }}
            >
              {isDark
                ? <LightMode sx={{ fontSize: 19, color: "warning.main" }} />
                : <DarkMode   sx={{ fontSize: 19, color: "text.secondary" }} />}
            </IconButton>
          </Tooltip>

          {/* Avatar */}
          <IconButton onClick={(e) => setUserMenuAnchor(e.currentTarget)} sx={{ p: 0, ml: 0.25 }}>
            <Avatar
              sx={{
                width: { xs: 34, sm: 38 },
                height: { xs: 34, sm: 38 },
                bgcolor: colors.avatarBg,
                color: "primary.contrastText",
                fontWeight: 700,
                fontSize: { xs: 13, sm: 15 },
                ...(!isDark && { boxShadow: `0 0 0 2px ${alpha(LIGHT.accent.main, 0.2)}` }),
              }}
            >
              {initial}
            </Avatar>
          </IconButton>
        </Box>
      </Box>

      {/* ── Date range popover ───────────────────────────────────────────────── */}
      <Popover
        open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            borderRadius: 3, p: 2,
            minWidth: { xs: 270, sm: 310 },
            mt: 0.75,
          },
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1.5, color: "text.primary" }}>
          Date Range
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
          {QUICK.map((q) => {
            const active = dateRangePreset === q.preset;
            return (
              <Chip
                key={q.label} label={q.label} size="small" clickable
                onClick={() => { applyDatePreset(q.preset); setAnchorEl(null); }}
                sx={{
                  borderRadius: 99, fontSize: 12.5, fontWeight: 600, height: 30,
                  bgcolor: active ? colors.accentSoft : colors.chipBg,
                  color: active ? colors.chipHoverColor : colors.chipColor,
                  border: "1px solid",
                  borderColor: active ? colors.chipHoverBorder : colors.chipBorder,
                  "&:hover": {
                    borderColor: colors.chipHoverBorder,
                    color: colors.chipHoverColor,
                    bgcolor: colors.accentSoft,
                  },
                  transition: "all 0.15s",
                }}
                variant="outlined"
              />
            );
          })}
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <TextField
            label="From" type="date" size="small" value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }} fullWidth
          />
          <TextField
            label="To" type="date" size="small" value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }} fullWidth
          />
        </Stack>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
          <AppButton
            intent="primary" size="small"
            onClick={() => setAnchorEl(null)}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, px: 2.5 }}
          >
            Apply
          </AppButton>
        </Box>
      </Popover>

      {/* ── User menu ────────────────────────────────────────────────────────── */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { minWidth: 185, mt: 0.75 } }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{user?.username || "Admin"}</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 12, mt: 0.25 }}>{user?.phone || ""}</Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        {/* The theme toggle now lives permanently in the top bar (all breakpoints),
            so the duplicate mobile-only menu item was removed. */}
        <MenuItem onClick={handleLogout} sx={{ fontSize: 13 }}>Sign out</MenuItem>
      </Menu>
    </Paper>
  );
}
